import axios, { AxiosResponse } from 'axios';
import { 
  User, 
  Order, 
  Product, 
  ApiResponse, 
  OrdersResponse, 
  NotificationsResponse,
  LoginRequest,
  RegisterRequest,
  CreateOrderRequest,
  UpdateOrderStatusRequest,
  AssignDriverRequest
} from '../types';

// Custom API Error class for better error handling
class ApiError extends Error {
  public response?: {
    status?: number;
    statusText?: string;
    data?: any;
  };

  constructor(message: string, response?: any) {
    super(message);
    this.name = 'ApiError';
    this.response = response;
    
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }
}

// API Configuration - Cloudflare Workers Backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://orderapp-backend.ssemrah33.workers.dev/api';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to get current tenant (from subdomain or URL parameter)
const getCurrentTenant = (): string | null => {
  const hostname = window.location.hostname;
  
  // First check URL parameter for testing
  const urlParams = new URLSearchParams(window.location.search);
  const tenantParam = urlParams.get('tenant');
  if (tenantParam) {
    return tenantParam;
  }
  
  // Skip for localhost and IPs
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost')) {
    return 'orderapp-frontendx'; // Default tenant for development
  }
  
  // Direct domain access or Cloudflare Pages deployment
  if (hostname.includes('orderapp-frontend') || hostname.includes('pages.dev')) {
    return 'orderapp-frontendx'; // Default tenant for Pages deployment
  }
  
  // Extract subdomain (e.g., "demo" from "demo.ordertrackingapp.com")
  const parts = hostname.split('.');
  if (parts.length > 2) {
    const subdomain = parts[0];
    // Skip www and api
    if (subdomain !== 'www' && subdomain !== 'api') {
      return subdomain;
    }
  }
  
  return 'orderapp-frontendx'; // Default fallback
};

// Request interceptor for authentication and tenant context
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add tenant context
    const tenant = getCurrentTenant();
    if (tenant) {
      // Add tenant as query parameter for tenant context
      config.params = config.params || {};
      config.params.tenant = tenant;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
    });
    
    // Create a proper error object with the backend error message
    if (error.response?.data?.error) {
      const customError = new ApiError(error.response.data.error, error.response);
      return Promise.reject(customError);
    }
    
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Generic API response handler
const handleApiResponse = <T>(response: AxiosResponse<ApiResponse<T>>): T => {
  if (response.data.error) {
    throw new ApiError(response.data.error, response);
  }
  return response.data.data;
};

// Auth API
export const authAPI = {
  login: async (credentials: LoginRequest): Promise<{ user: User; token: string }> => {
    const response = await apiClient.post('/auth/login', credentials);
    
    // The backend returns { success: true, token: "...", user: {...} } format
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    
    // Return the response directly since it already has the correct structure
    return response.data;
  },

  register: async (userData: RegisterRequest): Promise<{ user: User; token: string }> => {
    const response = await apiClient.post('/auth/register', userData);
    
    // The backend returns { success: true, token: "...", user: {...} } format
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    
    // Return the response directly since it already has the correct structure
    return response.data;
  },

  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout');
  },

  getProfile: async (): Promise<User> => {
    const response = await apiClient.get<ApiResponse<User>>('/auth/profile');
    return handleApiResponse(response);
  },

  updateProfile: async (userData: Partial<User>): Promise<User> => {
    const response = await apiClient.put<ApiResponse<User>>('/auth/profile', userData);
    return handleApiResponse(response);
  },

  getTenantInfo: async (subdomain?: string | null): Promise<any> => {
    let url = '/admin/company';
    const params: any = {};
    
    // If subdomain is provided, add it as a parameter
    if (subdomain) {
      params.tenant = subdomain;
    }
    
    try {
      const response = await apiClient.get<ApiResponse<any>>(url, { params });
      return handleApiResponse(response);
    } catch (error: any) {
      // If admin endpoint fails (403), try the public tenant endpoint
      if (error?.response?.status === 403) {
        console.log('Admin access denied, trying public tenant endpoint');
        const publicUrl = '/tenants/info';
        const publicResponse = await apiClient.get<ApiResponse<any>>(publicUrl, { params });
        return handleApiResponse(publicResponse);
      }
      throw error;
    }
  },
};

// Backend logs API
export const logsAPI = {
  getBackendLogs: async (): Promise<any> => {
    const response = await apiClient.get('/logs');
    return response.data;
  },

  getBackendHealth: async (): Promise<any> => {
    const response = await apiClient.get('/health');
    return response.data;
  },
};

// Orders API
export const ordersAPI = {
  getOrders: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<OrdersResponse> => {
    const response = await apiClient.get('/orders', { params });
    
    // The backend returns { success: true, orders: [...], pagination: {...} } format
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    
    // Return the response directly since it already has the correct structure
    return response.data;
  },

  getOrder: async (id: string): Promise<Order> => {
    const response = await apiClient.get(`/orders/${id}`);
    
    // The backend returns { success: true, data: {...} } format
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    
    // Return the data field from the response
    return response.data.data;
  },

  createOrder: async (orderData: CreateOrderRequest): Promise<Order> => {
    const response = await apiClient.post('/orders', orderData);
    
    // The backend returns { success: true, data: { id, order_number }, message: "..." } format
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    
    // Return the data field from the response
    return response.data.data;
  },

  updateOrder: async (id: string, orderData: Partial<Order>): Promise<Order> => {
    const response = await apiClient.put<ApiResponse<Order>>(`/orders/${id}`, orderData);
    return handleApiResponse(response);
  },

  updateOrderStatus: async (id: string, statusData: UpdateOrderStatusRequest): Promise<Order> => {
    const response = await apiClient.patch(`/orders/${id}/status`, statusData);
    
    // The backend returns { success: true, message: "..." } format
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    
    // Since the backend doesn't return the updated order, we need to fetch it
    return await ordersAPI.getOrder(id);
  },

  assignDriver: async (id: string, assignData: AssignDriverRequest): Promise<Order> => {
    const response = await apiClient.patch<ApiResponse<Order>>(`/orders/${id}/assign-driver`, assignData);
    return handleApiResponse(response);
  },

  getOrdersByRole: async (role: string, filters?: any): Promise<Order[]> => {
    const response = await apiClient.get(`/orders/role/${role}`, { params: filters });
    
    // The backend returns a complex object, not wrapped in ApiResponse
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    
    // Extract orders from the response - ensure it's always an array
    const orders = response.data.orders || [];
    return Array.isArray(orders) ? orders : [];
  },

  getDashboardStats: async (): Promise<any> => {
    const response = await apiClient.get('/dashboard/stats');
    
    // The backend returns { success: true, data: {...} } format
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    
    // Return the data field from the response
    return response.data.data;
  },

  getRecentOrders: async (limit: number = 5): Promise<Order[]> => {
    const response = await apiClient.get(`/orders/recent?limit=${limit}`);
    
    // The backend returns { success: true, data: [...] } format
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    
    // Return the data field from the response
    return response.data.data;
  },

  deleteOrder: async (id: string): Promise<void> => {
    await apiClient.delete(`/orders/${id}`);
  },
};

// Admin API
export const adminAPI = {
  getSystemLogs: async (params?: {
    page?: number;
    limit?: number;
    level?: string;
    search?: string;
  }): Promise<any> => {
    const response = await apiClient.get('/admin/logs', { params });
    
    // The backend returns { success: true, data: { logs: [...], pagination: {...} } } format
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    
    // Return the data field from the response
    return response.data.data;
  },

  getSystemStatus: async (): Promise<any> => {
    const response = await apiClient.get('/admin/system-status');
    
    // The backend returns { success: true, data: {...} } format
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    
    // Return the data field from the response
    return response.data.data;
  },
};

// Products API with comprehensive error handling and caching
export const productsAPI = {
  getProducts: async (filters?: {
    category?: string;
    brand?: string;
    status?: string;
    stock_status?: 'low' | 'out' | 'normal';
    search?: string;
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<{ data: Product[]; pagination: any }> => {
    try {
      const response = await apiClient.get('/products', { 
        params: filters 
      });
      
      // The backend returns { success: true, data: [...] } format
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      
      // Return the data field from the response
      return { data: response.data.data, pagination: {} };
    } catch (error) {
      console.error('Error fetching products:', error);
      throw new Error('Ürünler yüklenirken hata oluştu');
    }
  },

  getProduct: async (id: string): Promise<Product> => {
    try {
      const response = await apiClient.get<ApiResponse<Product>>(`/products/${id}`);
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error fetching product:', error);
      throw new Error('Ürün detayları yüklenirken hata oluştu');
    }
  },

  createProduct: async (productData: any): Promise<{ data: Product }> => {
    try {
      const response = await apiClient.post<ApiResponse<{ data: Product }>>('/products', productData);
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error creating product:', error);
      throw new Error('Ürün oluşturulurken hata oluştu');
    }
  },

  updateProduct: async (id: string, productData: any): Promise<{ data: Product }> => {
    try {
      const response = await apiClient.put<ApiResponse<{ data: Product }>>(`/products/${id}`, productData);
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error updating product:', error);
      throw new Error('Ürün güncellenirken hata oluştu');
    }
  },

  deleteProduct: async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`/products/${id}`);
    } catch (error) {
      console.error('Error deleting product:', error);
      throw new Error('Ürün silinirken hata oluştu');
    }
  },

  updateStock: async (id: string, stockData: {
    quantity: number;
    type: 'add' | 'subtract';
    reason: string;
    warehouse_id?: string;
    notes?: string;
  }): Promise<{ data: Product }> => {
    try {
      const response = await apiClient.patch<ApiResponse<{ data: Product }>>(`/products/${id}/stock`, stockData);
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error updating stock:', error);
      throw new Error('Stok güncellenirken hata oluştu');
    }
  },

  getCategories: async (): Promise<string[]> => {
    try {
      const response = await apiClient.get<ApiResponse<string[]>>('/products/categories');
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw new Error('Kategoriler yüklenirken hata oluştu');
    }
  },

  getBrands: async (): Promise<string[]> => {
    try {
      const response = await apiClient.get<ApiResponse<string[]>>('/products/brands');
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error fetching brands:', error);
      throw new Error('Markalar yüklenirken hata oluştu');
    }
  },

  getLowStockAlerts: async (): Promise<Product[]> => {
    try {
      const response = await apiClient.get<ApiResponse<Product[]>>('/products/low-stock');
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error fetching low stock alerts:', error);
      throw new Error('Düşük stok uyarıları yüklenirken hata oluştu');
    }
  },

  getStockMovements: async (productId: string, params?: {
    page?: number;
    limit?: number;
    type?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<any> => {
    try {
      const response = await apiClient.get<ApiResponse<any>>(`/products/${productId}/stock-movements`, { params });
      return handleApiResponse(response);
    } catch (error) {
      console.error('Error fetching stock movements:', error);
      throw new Error('Stok hareketleri yüklenirken hata oluştu');
    }
  },
};

// Notifications API
export const notificationsAPI = {
  getNotifications: async (params?: {
    page?: number;
    limit?: number;
    is_read?: boolean;
  }): Promise<NotificationsResponse> => {
    const response = await apiClient.get<ApiResponse<NotificationsResponse>>('/notifications', { params });
    return handleApiResponse(response);
  },

  markAsRead: async (id: string): Promise<void> => {
    await apiClient.patch(`/notifications/${id}/read`);
  },

  markAllAsRead: async (): Promise<void> => {
    await apiClient.patch('/notifications/mark-all-read');
  },

  deleteNotification: async (id: string): Promise<void> => {
    await apiClient.delete(`/notifications/${id}`);
  },
};

// Customers API
export const customersAPI = {
  getCustomers: async (params?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<any> => {
    const response = await apiClient.get('/customers', { params });
    
    // The backend returns { success: true, customers: [...] } format
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    
    // Return the response directly since it already has the correct structure
    return response.data;
  },

  getCustomer: async (id: string): Promise<any> => {
    const response = await apiClient.get<ApiResponse<any>>(`/customers/${id}`);
    return handleApiResponse(response);
  },

  createCustomer: async (customerData: any): Promise<any> => {
    const response = await apiClient.post<ApiResponse<any>>('/customers', customerData);
    return handleApiResponse(response);
  },

  updateCustomer: async (id: string, customerData: any): Promise<any> => {
    const response = await apiClient.put<ApiResponse<any>>(`/customers/${id}`, customerData);
    return handleApiResponse(response);
  },

  deleteCustomer: async (id: string): Promise<void> => {
    await apiClient.delete(`/customers/${id}`);
  },
};

// Invoices API
export const invoicesAPI = {
  getInvoices: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<any> => {
    const response = await apiClient.get<ApiResponse<any>>('/invoices', { params });
    return handleApiResponse(response);
  },

  getInvoice: async (id: string): Promise<any> => {
    const response = await apiClient.get<ApiResponse<any>>(`/invoices/${id}`);
    return handleApiResponse(response);
  },

  createInvoice: async (invoiceData: any): Promise<any> => {
    const response = await apiClient.post<ApiResponse<any>>('/invoices', invoiceData);
    return handleApiResponse(response);
  },

  updateInvoice: async (id: string, invoiceData: any): Promise<any> => {
    const response = await apiClient.put<ApiResponse<any>>(`/invoices/${id}`, invoiceData);
    return handleApiResponse(response);
  },

  deleteInvoice: async (id: string): Promise<void> => {
    await apiClient.delete(`/invoices/${id}`);
  },
};

// Tenants API for admin management
export const tenantsAPI = {
  getTenants: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }): Promise<any> => {
    const response = await apiClient.get<ApiResponse<any>>('/admin/tenants', { params });
    return handleApiResponse(response);
  },

  getTenant: async (tenantId: string): Promise<any> => {
    const response = await apiClient.get<ApiResponse<any>>(`/admin/tenants/${tenantId}`);
    return handleApiResponse(response);
  },

  createTenant: async (tenantData: {
    company_name: string;
    subdomain: string;
    subscription_plan: 'free' | 'basic' | 'pro' | 'enterprise';
    contact_info?: any;
    branding?: any;
    settings?: any;
  }): Promise<any> => {
    const response = await apiClient.post<ApiResponse<any>>('/admin/tenants', tenantData);
    return handleApiResponse(response);
  },

  updateTenant: async (tenantId: string, tenantData: any): Promise<any> => {
    const response = await apiClient.put<ApiResponse<any>>(`/admin/tenants/${tenantId}`, tenantData);
    return handleApiResponse(response);
  },

  deleteTenant: async (tenantId: string): Promise<void> => {
    await apiClient.delete(`/admin/tenants/${tenantId}`);
  },

  updateTenantSettings: async (tenantId: string, settings: any): Promise<any> => {
    const response = await apiClient.put<ApiResponse<any>>(`/admin/tenants/${tenantId}/settings`, settings);
    return handleApiResponse(response);
  },

  getTenantUsage: async (tenantId: string): Promise<any> => {
    const response = await apiClient.get<ApiResponse<any>>(`/admin/tenants/${tenantId}/usage`);
    return handleApiResponse(response);
  },

  // DNS yönetimi endpoint'leri
  setupDNSForExistingTenants: async (): Promise<any> => {
    const response = await apiClient.post<ApiResponse<any>>('/tenants/admin/dns/setup-existing');
    return handleApiResponse(response);
  },

  setupWildcardDNS: async (): Promise<any> => {
    const response = await apiClient.post<ApiResponse<any>>('/tenants/admin/dns/setup-wildcard');
    return handleApiResponse(response);
  },

  listDNSRecords: async (): Promise<any> => {
    const response = await apiClient.get<ApiResponse<any>>('/tenants/admin/dns/list');
    return handleApiResponse(response);
  },

  checkTenantDNSStatus: async (tenantId: string): Promise<any> => {
    const response = await apiClient.get<ApiResponse<any>>(`/tenants/admin/dns/check/${tenantId}`);
    return handleApiResponse(response);
  },
};

export default apiClient;

// Genel API servisi - Sales.tsx ve diğer bileşenler için
export const apiService = {
  get: async <T>(url: string, config?: any): Promise<{ data: T }> => {
    const response = await apiClient.get<T>(url, config);
    return { data: response.data };
  },

  post: async <T>(url: string, data?: any, config?: any): Promise<T> => {
    const response = await apiClient.post<T>(url, data, config);
    return response.data;
  },

  put: async <T>(url: string, data?: any, config?: any): Promise<T> => {
    const response = await apiClient.put<T>(url, data, config);
    return response.data;
  },

  patch: async <T>(url: string, data?: any, config?: any): Promise<T> => {
    const response = await apiClient.patch<T>(url, data, config);
    return response.data;
  },

  delete: async <T>(url: string, config?: any): Promise<T> => {
    const response = await apiClient.delete<T>(url, config);
    return response.data;
  },
}; 